/**
 * The role hierarchy, once.
 *
 * <h3>Why this file exists</h3>
 *
 * This map had five copies — AuthContext, RoleGuard, TeamPageV2 (as
 * ROLE_LEVELS_FE), and the role-name list again in TeamPage and the two member
 * managers. They drifted, twice, and each drift was a real lockout:
 *
 *   - Platform Admin sat at 5 in two of them while the backend carried 6,
 *     which conflated the only global wildcard with a customer-scoped one.
 *   - Approver / Compliance / Operator / Auditor were missing from RoleGuard
 *     entirely, so `ROLE_LEVELS[role]` was undefined, the `|| 0` fallback put
 *     those roles below Viewer, and every minRole gate locked them out of
 *     pages they own (DEF-FE-ROUTE-AGENT-DETAIL-MINROLE-001).
 *
 * A fifth copy for a sixth role was not worth adding. One module, imported
 * everywhere, with `roleLevels.test.js` asserting it equals the backend's
 * `RbacService.ROLE_LEVELS` entry for entry, in both directions.
 *
 * <h3>What is authoritative</h3>
 *
 * The server is. `RbacService.ROLE_LEVELS` enforces every rank gate, and
 * `GET /api/v1/rbac/roles` (`roleCatalog`) serves the levels at runtime,
 * including org-scoped CUSTOM roles that cannot be known at build time. This
 * map is the compile-time mirror of the BUILT-IN roles only: a role absent from
 * it is not "level 0", it is a role whose level has to come from the server.
 * Callers that may see a custom role pass its server-given level in rather than
 * reading it here.
 */

/** Built-in role → hierarchy level. Mirrors backend RbacService.ROLE_LEVELS. */
export const ROLE_LEVELS = Object.freeze({
  // Platform Admin (6) is the ONLY global `*` and sits ABOVE Super Admin (5),
  // whose `*` is bounded to its own customer(s). They are not peers.
  'Platform Admin': 6,
  'Super Admin': 5,
  'Org Admin': 4,
  'Tenant Admin': 3,
  // Prove7 Protect — the self-serve author-and-run role for the Protect
  // surface, at Tenant Admin HEIGHT and deliberately not above it: Org Admin
  // height is the tier that manages roles, which would be an escalation path
  // out of the role. See the long note in RbacService.ROLE_LEVELS.
  'Protect': 3,
  // Instance tier and the functional roles are numeric peers of Developer;
  // scope precision lives in the grant row (tenant_id + instance_id), not here.
  'Instance Admin': 2,
  'Promoter': 2,
  'Developer': 2,
  'Approver': 2,
  'Compliance': 2,
  'Operator': 2,
  // Read-only tier.
  'Auditor': 1,
  'Viewer': 1,
});

/**
 * The built-in roles, ordered by tier: Platform → Customer → Org → Tenant →
 * Instance → Functional → Read-only. This is the order a role picker renders.
 */
export const ROLE_OPTIONS = Object.freeze([
  'Platform Admin',
  // Customer tier (the legacy 'Super Admin' name is preserved for backward
  // compatibility; V208 maps it to Customer Super Admin semantically).
  'Super Admin',
  'Org Admin',
  'Tenant Admin',
  'Protect',
  'Instance Admin',
  'Promoter',
  'Developer',
  'Approver',
  'Compliance',
  'Operator',
  'Auditor',
  'Viewer',
]);

/**
 * The subset a scope-level member manager offers by default: everything except
 * the two wildcard tiers, which are granted from the platform surfaces rather
 * than from an org / tenant / instance member list.
 */
export const GRANTABLE_ROLE_OPTIONS = Object.freeze(
  ROLE_OPTIONS.filter((r) => r !== 'Platform Admin' && r !== 'Super Admin'),
);

/** Level of a role for comparing against someone's own, unknown → 0 (no rank). */
export const roleLevel = (role) => ROLE_LEVELS[role] ?? 0;

/**
 * Level of a role named by a GATE, unknown → 99. The asymmetry is deliberate
 * and matches the backend's two defaults: an unrecognised caller must not clear
 * a gate (0), and an unrecognised gate must not be cleared by anybody (99), so
 * a typo in either position fails closed.
 */
export const gateLevel = (role) => ROLE_LEVELS[role] ?? 99;
